/**
 * boundClavicleInfluence — ADR-021 Stage B, the CURE: geodesic-voxel bounded
 * clavicle influence + weight inpainting to deliberate spine donors (Dionne &
 * de Lasa 2013 for the bound's metric; Abdrashitov/Epic 2023 for the
 * mesh-graph inpaint). ONE responsibility: given a mesh + skin + resolved
 * chest cast + voxel volume, return REPAIRED weights with bounded per-clavicle
 * support and a typed repair report. Pure function of its inputs — no I/O, no
 * scene, no three (ADR-021 §3/§4).
 *
 * THE BOUND IS A CAP, NOT A HAIRCUT — the idempotence law (§9) decides this.
 * Each clavicle's legitimate support is an envelope over geodesic distance d
 * from its DISTAL seat (weights/support-field): full ownership allowed inside
 * `bound − feather`, smoothstepped to zero at `bound`, both derived from the
 * measured clavicle length — never tuned per rig (§2). The repaired weight is
 * `min(w, envelope(d))`: a second run finds every weight already under its
 * envelope and reports `not-needed` — a projection, bit-stable under repeat.
 * The smoothstep feather is also what keeps the weight-field Laplacian bounded
 * at the cull boundary (no clamp-seam crease — survey warning #1).
 *
 * TWO ARMED CLASSES, ONE ENVELOPE (calibrated on the acceptance corpus): the
 * CLAVICLES are bounded everywhere, and the rest of the ARM CHAIN (upper arm
 * on down) is bounded ONLY inside the chest-band box — ADR-021 §1 names both
 * diseases (28% clavicle at the sternum AND 37–46% arm-chain mass across the
 * band), and the four-rig calibration measured the clavicle-only cure
 * flattening at ~−7% collapse: the residual was the arm bleed. The band-box
 * restriction is what keeps the arm's LEGITIMATE territory untouched — in
 * both T- and A-pose the limb itself lies outside the band laterally or
 * below it, so only medial-chest arm bleed is in reach.
 *
 * FREED MASS FLOWS TO THE DELIBERATE DONORS — the spine chain the resolver
 * named — never proportionally to whatever else sat in the vertex's palette
 * (survey warning #3, the anti-"clamp-and-renormalize" law). The donor MIX per
 * vertex is the vertex's own trusted spine weights where it has any; where it
 * has none, the mix is INPAINTED by Jacobi diffusion over the mesh graph from
 * neighbouring trusted mixes (the Epic-2023 move), with the nearest donor as
 * the loudly-marked last resort.
 *
 * Refusal ≠ not-needed ≠ repaired (§7): non-mirroring clavicle lengths, a
 * distal seed the voxelization cannot seat, a donor missing from the skin
 * palette, or a repair that would move more than `maxDonorFlowFraction` of
 * total mass all return the typed `refused` outcome — the caller ships the
 * file UNREPAIRED with the named reasons. Structural garbage (broken skin
 * arrays, a grid with no world embedding) throws — that is a wiring bug, not
 * a product outcome.
 *
 * Depends on: weights/support-field, weights/chest-bones, weights/skin-data,
 * hands/voxel-grid (VolumeGrid), hands/separation-profile (EvidenceMarker).
 * Depended on by: kernel index, the adapter-three applicator, the bake lint.
 * Provenance: docs/adr/ADR-021-chest-integrity.md · the forensics in
 * docs/research/CHEST_COLLAPSE_SKINNING_SURVEY_2026-08-09.md ·
 * scripts/chest-collapse-probe.mjs.
 */
import { KernelError } from '../ir/errors.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import type { VolumeGrid } from '../hands/voxel-grid.js';
import { vec3Dot, vec3Normalize } from '../math/vec3.js';
import { CHEST_BAND_CAP_FRACTION, type ChestBones } from './chest-bones.js';
import { computeClavicleSupportFields, type ClavicleSupportField } from './support-field.js';
import { INFLUENCES_PER_VERTEX, skinVertexCount, validateSkinData, type SkinVertexData } from './skin-data.js';

/** A cannot-run condition — caller/wiring bug, fail-loud. */
export class BoundInfluenceError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(
      `Cannot bound clavicle influence (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

export interface BoundInfluenceOptions {
  /** Support bound as a fraction of clavicle length, measured geodesically
   * from the acromion seed ball (default 0.65, calibrated on the four-rig
   * acceptance corpus: the deltoid ball sits within ~0.4, the diseased
   * mid-pec at ~0.6–0.8, the sternum at ~1.2). */
  readonly boundFactor?: number;
  /** Smoothstep taper width as a fraction of clavicle length (default 0.3). */
  readonly featherFactor?: number;
  /** Max relative L/R clavicle-length mismatch before refusing (default 0.15). */
  readonly mirrorTolerance?: number;
  /** Refuse when freed mass exceeds this fraction of total mass (default
   * 0.20). Was 0.15 — the measured male culls ~11% across both armed classes —
   * until Shirley (Allen's Tripo female, 2026-09-29, "the undiagnosed underarm
   * gap"): droop −13.3% / raise +11.2%, the defect itself, refused at 18.5%;
   * at 0.20 she repairs to 0.0% / 0.0%, cap 0.89. Census of 25 bodies at 0.15
   * vs 0.20: only Shirley changes (the corpus maximum otherwise is the goblin's
   * 12.0%), every repair lands inside §9. */
  readonly maxDonorFlowFraction?: number;
  /** Refuse when a distal seed snapped farther than this many voxels (default 3). */
  readonly seedSnapLimitVoxels?: number;
  /** Jacobi passes for the donor-mix inpaint (default 32). */
  readonly diffusionIterations?: number;
}

/** One side's repair accounting — the §3 report, per clavicle. */
export interface ClavicleRepairSide {
  readonly side: 'left' | 'right';
  readonly clavicleRigBone: string;
  readonly clavicleLength: number;
  /** The envelope, world units. */
  readonly bound: number;
  readonly feather: number;
  /** Weight mass removed from this side's armed bones (= mass its donors received). */
  readonly culledMass: number;
  /** Portion of culledMass that came from the arm-chain class (band-box only). */
  readonly armChainCulledMass: number;
  /** Post-repair 95th-pct geodesic reach of remaining weight, clavicle lengths. */
  readonly residualSupportExtent: number;
}

export interface BoundInfluenceReport {
  readonly sides: readonly [ClavicleRepairSide, ClavicleRepairSide];
  /** Total weight mass in the skin (the flow denominators). */
  readonly totalMass: number;
  /** Freed mass as a fraction of totalMass. */
  readonly culledMassFraction: number;
  /** Mass received per donor rig bone, both sides pooled. */
  readonly donorFlows: readonly { readonly rigBone: string; readonly mass: number }[];
  /** Mass reassigned to the largest kept influence when 4 slots overflowed. */
  readonly droppedSlotMass: number;
  readonly markers: readonly EvidenceMarker[];
}

export type BoundInfluenceOutcome =
  | {
      readonly kind: 'repaired';
      readonly jointIndices: Uint16Array;
      readonly jointWeights: Float32Array;
      readonly report: BoundInfluenceReport;
    }
  | { readonly kind: 'not-needed'; readonly report: BoundInfluenceReport }
  | { readonly kind: 'refused'; readonly reasons: readonly string[]; readonly markers: readonly EvidenceMarker[] };

/** Freed mass below this fraction of total mass = the file never needed repair. */
const NOT_NEEDED_FRACTION = 1e-4;
/** A vertex "has a donor mix of its own" above this much donor mass. */
const DONOR_MASS_FLOOR = 1e-4;
/** Weight floor for the residual-extent read (matches the gate's default). */
const SUPPORT_WEIGHT_FLOOR = 0.05;

/**
 * Repair one skin's clavicle weights. See the module header for the whole
 * contract; `indices` is the triangle list (null = non-indexed triples), used
 * only to build the inpaint diffusion graph.
 */
export function boundClavicleInfluence(
  grid: VolumeGrid,
  skin: SkinVertexData,
  indices: ArrayLike<number> | null,
  bones: ChestBones,
  options: BoundInfluenceOptions = {},
): BoundInfluenceOutcome {
  validateSkinData(skin);
  const boundFactor = options.boundFactor ?? 0.55;
  const featherFactor = options.featherFactor ?? 0.3;
  const mirrorTolerance = options.mirrorTolerance ?? 0.15;
  const maxDonorFlowFraction = options.maxDonorFlowFraction ?? 0.2;
  const seedSnapLimitVoxels = options.seedSnapLimitVoxels ?? 3;
  const diffusionIterations = options.diffusionIterations ?? 32;
  const issues: string[] = [];
  if (!(boundFactor > 0)) issues.push(`boundFactor must be > 0, got ${boundFactor}`);
  if (!(featherFactor > 0 && featherFactor < boundFactor)) {
    issues.push(`featherFactor must be in (0, boundFactor), got ${featherFactor}`);
  }
  if (!(mirrorTolerance >= 0)) issues.push(`mirrorTolerance must be >= 0, got ${mirrorTolerance}`);
  if (!(maxDonorFlowFraction > 0 && maxDonorFlowFraction <= 1)) {
    issues.push(`maxDonorFlowFraction must be in (0, 1], got ${maxDonorFlowFraction}`);
  }
  if (!Number.isInteger(diffusionIterations) || diffusionIterations < 1) {
    issues.push(`diffusionIterations must be a positive integer, got ${diffusionIterations}`);
  }
  if (issues.length > 0) throw new BoundInfluenceError(issues);

  const n = skinVertexCount(skin);
  const K = INFLUENCES_PER_VERTEX;

  // --- donor palette indices: a donor the skin cannot address is a refusal ---
  const paletteIndex = new Map<string, number>();
  skin.jointNames.forEach((name, i) => {
    if (!paletteIndex.has(name)) paletteIndex.set(name, i);
  });
  const donors = bones.donors.filter((d) => paletteIndex.has(d.rigBone));
  if (donors.length === 0) {
    return {
      kind: 'refused',
      reasons: [
        `no donor bone is addressable in the skin palette (donors: ${bones.donors.map((d) => d.rigBone).join(', ')})`,
      ],
      markers: [],
    };
  }

  // --- support fields + the refusal gates (§7) ---
  const fields = computeClavicleSupportFields(grid, skin, bones);
  const markers: EvidenceMarker[] = [...fields.markers];
  const reasons: string[] = [];
  const lenL = fields.left.clavicleLength;
  const lenR = fields.right.clavicleLength;
  const meanLen = (lenL + lenR) / 2;
  if (!(meanLen > 0)) reasons.push('degenerate clavicle segments (zero length) — no bound can be derived');
  else if (Math.abs(lenL - lenR) / meanLen > mirrorTolerance) {
    reasons.push(
      `non-mirroring bounds: clavicle lengths L=${lenL.toFixed(4)} R=${lenR.toFixed(4)} differ by ` +
        `${((100 * Math.abs(lenL - lenR)) / meanLen).toFixed(1)}% > ${(mirrorTolerance * 100).toFixed(0)}%`,
    );
  }
  for (const f of [fields.left, fields.right]) {
    if (f.seedSnapDistanceVoxels > seedSnapLimitVoxels) {
      reasons.push(
        `${f.side} clavicle distal seed is ${f.seedSnapDistanceVoxels.toFixed(1)} voxels off the volume ` +
          `(limit ${seedSnapLimitVoxels}) — voxelization cannot seat this bone`,
      );
    }
  }
  if (reasons.length > 0) return { kind: 'refused', reasons, markers };

  // Mirror-pair validation passed; the envelope itself is derived from the
  // SHARED mean length so both sides cure as a pair by construction (§2.4).
  const bound = boundFactor * meanLen;
  const feather = featherFactor * meanLen;

  // --- measure the excess: per armed class, per vertex, weight above its envelope ---
  const envelope = (d: number): number => {
    if (!Number.isFinite(d)) return 0; // unreachable volume holds no legitimate support
    if (d <= bound - feather) return 1;
    if (d >= bound) return 0;
    const t = (bound - d) / feather;
    return t * t * (3 - 2 * t); // smoothstep — C¹, the Laplacian-bounded taper
  };

  // Palette classification: which armed class (if any) each joint belongs to.
  const CLAV_L = 0;
  const CLAV_R = 1;
  const ARM_L = 2;
  const ARM_R = 3;
  const paletteClass = skin.jointNames.map((name): number => {
    if (name === bones.clavicleL.rigBone) return CLAV_L;
    if (name === bones.clavicleR.rigBone) return CLAV_R;
    if (bones.subtreeL.has(name)) return ARM_L;
    if (bones.subtreeR.has(name)) return ARM_R;
    return -1;
  });
  const fieldOf = (cls: number): ClavicleSupportField => (cls === CLAV_L || cls === ARM_L ? fields.left : fields.right);

  // The chest-band box, in the rig's declared triad — the arm-chain classes
  // are armed ONLY here (the limb's own territory lies outside it). Lateral
  // coordinates are measured from the CHEST CENTER (the acromion midpoint),
  // never the world origin: a rig parked off-axis would otherwise see both
  // arms on one side and the band miss the chest entirely (Codex P1, #86 —
  // every specimen happens to sit at origin, so nothing else caught it).
  const up = vec3Normalize(bones.up);
  const lateral = vec3Normalize(bones.lateral);
  const centerLateral =
    (vec3Dot(bones.armL.worldPosition, lateral) + vec3Dot(bones.armR.worldPosition, lateral)) / 2;
  const hLow = vec3Dot(bones.bandBottom.worldPosition, up);
  const hHigh = vec3Dot(bones.bandTop.worldPosition, up);
  const capLateral =
    Math.abs(vec3Dot(bones.armL.worldPosition, lateral) - centerLateral) * CHEST_BAND_CAP_FRACTION;
  const inBand = new Uint8Array(n);
  for (let v = 0; v < n; v += 1) {
    const x = skin.positions[v * 3]!;
    const y = skin.positions[v * 3 + 1]!;
    const z = skin.positions[v * 3 + 2]!;
    const h = x * up.x + y * up.y + z * up.z;
    if (h < hLow || h > hHigh) continue;
    const l = Math.abs(x * lateral.x + y * lateral.y + z * lateral.z - centerLateral);
    if (l <= capLateral) inBand[v] = 1;
  }

  /** The cap this slot's weight must sit under (1 = unarmed / out of reach). */
  const slotCap = (v: number, cls: number): number => {
    if (cls < 0) return 1;
    if ((cls === ARM_L || cls === ARM_R) && inBand[v] !== 1) return 1;
    return envelope(fieldOf(cls).distances[v]!);
  };

  const freed = new Float64Array(n); // per-vertex total freed mass (both sides)
  const sideFreed = [0, 0] as [number, number];
  const sideArmFreed = [0, 0] as [number, number];
  let totalMass = 0;
  const slotAgg = new Map<number, number>(); // per-vertex scratch: joint → summed weight
  for (let v = 0; v < n; v += 1) {
    // Aggregate duplicate palette slots FIRST: a vertex may legally repeat a
    // joint index across JOINTS_0 slots, and the envelope caps the BONE's
    // aggregate ownership — two 0.3 slots under a 0.4 cap are 0.6 of excess
    // territory, not two innocents (Codex P2, #86; matches the repack, which
    // already merges duplicates).
    slotAgg.clear();
    for (let k = 0; k < K; k += 1) {
      const w = skin.jointWeights[v * K + k]!;
      if (w === 0) continue;
      totalMass += w;
      const j = skin.jointIndices[v * K + k]!;
      slotAgg.set(j, (slotAgg.get(j) ?? 0) + w);
    }
    for (const [j, w] of slotAgg) {
      const cls = paletteClass[j]!;
      const excess = Math.max(0, w - slotCap(v, cls));
      if (excess > 0) {
        freed[v]! += excess;
        const side = cls === CLAV_L || cls === ARM_L ? 0 : 1;
        sideFreed[side] += excess;
        if (cls === ARM_L || cls === ARM_R) sideArmFreed[side] += excess;
      }
    }
  }
  const totalFreed = sideFreed[0] + sideFreed[1];
  const culledMassFraction = totalMass > 0 ? totalFreed / totalMass : 0;

  const sideReport = (
    f: ClavicleSupportField,
    culled: number,
    armCulled: number,
    indicesView: ArrayLike<number>,
    weightsView: ArrayLike<number>,
  ): ClavicleRepairSide => ({
    side: f.side,
    clavicleRigBone: f.clavicle.rigBone,
    clavicleLength: f.clavicleLength,
    bound,
    feather,
    culledMass: culled,
    armChainCulledMass: armCulled,
    residualSupportExtent: residualExtent(skin.jointNames, indicesView, weightsView, f, n, K),
  });

  if (culledMassFraction < NOT_NEEDED_FRACTION) {
    return {
      kind: 'not-needed',
      report: {
        sides: [
          sideReport(fields.left, 0, 0, skin.jointIndices, skin.jointWeights),
          sideReport(fields.right, 0, 0, skin.jointIndices, skin.jointWeights),
        ],
        totalMass,
        culledMassFraction,
        donorFlows: [],
        droppedSlotMass: 0,
        markers,
      },
    };
  }
  if (culledMassFraction > maxDonorFlowFraction) {
    return {
      kind: 'refused',
      reasons: [
        `repair would move ${(100 * culledMassFraction).toFixed(1)}% of total weight mass ` +
          `(limit ${(100 * maxDonorFlowFraction).toFixed(0)}%) — this rig is outside the defect class this bound models`,
      ],
      markers,
    };
  }

  // --- donor mix per repaired vertex: own trusted spine weights, else inpaint ---
  const donorCount = donors.length;
  const ownMix = (v: number): Float64Array | null => {
    const mix = new Float64Array(donorCount);
    let mass = 0;
    for (let k = 0; k < K; k += 1) {
      const w = skin.jointWeights[v * K + k]!;
      if (w === 0) continue;
      const name = skin.jointNames[skin.jointIndices[v * K + k]!]!;
      const d = donors.findIndex((dn) => dn.rigBone === name);
      if (d >= 0) {
        mix[d]! += w;
        mass += w;
      }
    }
    if (mass < DONOR_MASS_FLOOR) return null;
    for (let d = 0; d < donorCount; d += 1) mix[d]! /= mass;
    return mix;
  };
  const mixes = new Array<Float64Array | null>(n).fill(null);
  const needsInpaint: number[] = [];
  for (let v = 0; v < n; v += 1) {
    if (freed[v]! <= 0) continue;
    const own = ownMix(v);
    if (own !== null) mixes[v] = own;
    else needsInpaint.push(v);
  }
  let defaulted = 0;
  if (needsInpaint.length > 0) {
    defaulted = inpaintDonorMixes(skin, indices, freed, mixes, needsInpaint, donorCount, ownMix, diffusionIterations);
    markers.push({ code: 'donor-mix-inpainted', detail: { count: needsInpaint.length - defaulted } });
    if (defaulted > 0) {
      // Vertices no trusted mix could reach — the nearest donor takes them, loudly.
      markers.push({ code: 'donor-mix-defaulted', detail: { count: defaulted, donor: donors[0]!.rigBone } });
    }
  }

  // --- apply: cap the clavicles, flow freed mass to donors, repack 4 slots ---
  const outIndices = new Uint16Array(n * K);
  const outWeights = new Float32Array(n * K);
  const donorFlow = new Float64Array(donorCount);
  const donorPaletteIdx = donors.map((d) => paletteIndex.get(d.rigBone)!);
  const isDonorIdx = new Set(donorPaletteIdx);
  let droppedSlotMass = 0;
  for (let v = 0; v < n; v += 1) {
    // Accumulate the vertex's final bone→weight map.
    const entries = new Map<number, number>();
    for (let k = 0; k < K; k += 1) {
      const w = skin.jointWeights[v * K + k]!;
      if (w === 0) continue;
      const j = skin.jointIndices[v * K + k]!;
      const kept = Math.min(w, slotCap(v, paletteClass[j]!));
      if (kept > 0) entries.set(j, (entries.get(j) ?? 0) + kept);
    }
    const freedHere = freed[v]!;
    // Donor bases BEFORE the freed mass arrives — the flow ledger reports
    // final − base per donor, so it stays exact through the repack below
    // (Codex P1, #86: the pre-repack ledger claimed mass the repack then
    // handed to a non-donor).
    const donorBase = donorPaletteIdx.map((j) => entries.get(j) ?? 0);
    if (freedHere > 0) {
      const mix = mixes[v] ?? defaultMix(donorCount);
      for (let d = 0; d < donorCount; d += 1) {
        const m = freedHere * mix[d]!;
        if (m <= 0) continue;
        const j = donorPaletteIdx[d]!;
        entries.set(j, (entries.get(j) ?? 0) + m);
      }
    }
    // Deterministic 4-slot repack: keep the heaviest four (palette index breaks
    // ties) — but when this vertex was repaired, a DONOR slot is reserved: if
    // no donor survived the cut, the heaviest donor replaces the lightest kept
    // entry, and any overflow remainder lands on the heaviest KEPT DONOR —
    // never on an unrelated arm/accessory bone (the deliberate-donor law,
    // survey warning #3; Codex P1, #86). Sum is conserved either way.
    const sorted = [...entries.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
    const keptEntries = sorted.slice(0, K);
    const droppedEntries = sorted.slice(K);
    if (freedHere > 0 && !keptEntries.some(([j]) => isDonorIdx.has(j))) {
      const donorEntry = droppedEntries.find(([j]) => isDonorIdx.has(j));
      if (donorEntry !== undefined && keptEntries.length > 0) {
        const displaced = keptEntries[keptEntries.length - 1]!;
        keptEntries[keptEntries.length - 1] = donorEntry;
        droppedEntries[droppedEntries.indexOf(donorEntry)] = displaced;
      }
    }
    let remainder = 0;
    for (const [, w] of droppedEntries) remainder += w;
    if (remainder > 0 && keptEntries.length > 0) {
      let target = 0;
      if (freedHere > 0) {
        for (let i = 0; i < keptEntries.length; i += 1) {
          if (isDonorIdx.has(keptEntries[i]![0])) {
            target = i;
            break;
          }
        }
      }
      keptEntries[target] = [keptEntries[target]![0], keptEntries[target]![1] + remainder];
      droppedSlotMass += remainder;
    }
    // Flow ledger (repaired vertices only): what each donor ACTUALLY holds
    // now, minus what it held before repair — negative when a tiny donor
    // entry folded into the reserved one; the per-vertex sum is freedHere
    // plus any overflow remainder the donor absorbed.
    if (freedHere > 0) {
      for (let d = 0; d < donorCount; d += 1) {
        const j = donorPaletteIdx[d]!;
        const final = keptEntries.find(([jj]) => jj === j)?.[1] ?? 0;
        donorFlow[d]! += final - donorBase[d]!;
      }
    }
    for (let k = 0; k < K; k += 1) {
      outIndices[v * K + k] = keptEntries[k]?.[0] ?? 0;
      outWeights[v * K + k] = keptEntries[k]?.[1] ?? 0;
    }
  }
  if (droppedSlotMass > 0) {
    markers.push({ code: 'weight-slots-overflowed', detail: { massReassigned: Number(droppedSlotMass.toFixed(6)) } });
  }

  return {
    kind: 'repaired',
    jointIndices: outIndices,
    jointWeights: outWeights,
    report: {
      sides: [
        sideReport(fields.left, sideFreed[0], sideArmFreed[0], outIndices, outWeights),
        sideReport(fields.right, sideFreed[1], sideArmFreed[1], outIndices, outWeights),
      ],
      totalMass,
      culledMassFraction,
      donorFlows: donors.map((d, i) => ({ rigBone: d.rigBone, mass: donorFlow[i]! })).filter((f) => f.mass > 0),
      droppedSlotMass,
      markers,
    },
  };
}

/** Uniform-on-first-donor fallback mix (donor[0] = the nearest spine ancestor). */
function defaultMix(donorCount: number): Float64Array {
  const mix = new Float64Array(donorCount);
  mix[0] = 1;
  return mix;
}

/**
 * Post-repair 95th-pct geodesic reach of a clavicle's weight, clavicle lengths.
 * `indices`/`weights` must be the SAME generation — the repack reorders slots
 * (the first law-test run caught a pre-repair-indices/post-repair-weights mix).
 */
function residualExtent(
  jointNames: readonly string[],
  indices: ArrayLike<number>,
  weights: ArrayLike<number>,
  f: ClavicleSupportField,
  n: number,
  K: number,
): number {
  const reach: number[] = [];
  for (let v = 0; v < n; v += 1) {
    let w = 0;
    for (let k = 0; k < K; k += 1) {
      if (jointNames[indices[v * K + k]!] === f.clavicle.rigBone) w += weights[v * K + k]!;
    }
    if (w < SUPPORT_WEIGHT_FLOOR) continue;
    const d = f.distances[v]!;
    if (Number.isFinite(d)) reach.push(d);
  }
  if (reach.length === 0 || f.clavicleLength <= 0) return 0;
  reach.sort((a, b) => a - b);
  return reach[Math.min(reach.length - 1, Math.floor(reach.length * 0.95))]! / f.clavicleLength;
}

/**
 * Jacobi-diffuse donor mixes over the mesh graph into the vertices that have
 * no spine mass of their own. Known values (a vertex's own trusted mix) stay
 * fixed; unknowns average their neighbours' latest mixes. Returns how many
 * vertices NO mix could reach (they take the nearest-donor default).
 */
function inpaintDonorMixes(
  skin: SkinVertexData,
  indices: ArrayLike<number> | null,
  freed: Float64Array,
  mixes: Array<Float64Array | null>,
  needsInpaint: readonly number[],
  donorCount: number,
  ownMix: (v: number) => Float64Array | null,
  iterations: number,
): number {
  const need = new Set(needsInpaint);
  // Adjacency restricted to edges touching an inpaint vertex — the rest of the
  // mesh only matters through the boundary ring, captured by resolving each
  // outside neighbour's own mix on demand.
  const nbrs = new Map<number, number[]>();
  const addEdge = (a: number, b: number): void => {
    if (need.has(a)) {
      const list = nbrs.get(a);
      if (list === undefined) nbrs.set(a, [b]);
      else if (!list.includes(b)) list.push(b);
    }
  };
  const triCount = indices === null ? Math.floor(skin.positions.length / 9) : Math.floor(indices.length / 3);
  for (let t = 0; t < triCount; t += 1) {
    const a = indices === null ? t * 3 : indices[t * 3]!;
    const b = indices === null ? t * 3 + 1 : indices[t * 3 + 1]!;
    const c = indices === null ? t * 3 + 2 : indices[t * 3 + 2]!;
    addEdge(a, b);
    addEdge(a, c);
    addEdge(b, a);
    addEdge(b, c);
    addEdge(c, a);
    addEdge(c, b);
  }
  // Boundary neighbours outside the inpaint set contribute their own mix.
  const boundaryMix = new Map<number, Float64Array | null>();
  const mixOf = (v: number): Float64Array | null => {
    if (need.has(v)) return mixes[v] ?? null;
    let m = boundaryMix.get(v);
    if (m === undefined) {
      m = freed[v]! > 0 ? mixes[v] ?? null : ownMix(v);
      boundaryMix.set(v, m);
    }
    return m;
  };
  for (let it = 0; it < iterations; it += 1) {
    const next = new Map<number, Float64Array>();
    let changed = false;
    for (const v of needsInpaint) {
      const acc = new Float64Array(donorCount);
      let count = 0;
      for (const u of nbrs.get(v) ?? []) {
        const m = mixOf(u);
        if (m === null) continue;
        for (let d = 0; d < donorCount; d += 1) acc[d]! += m[d]!;
        count += 1;
      }
      if (count === 0) continue;
      for (let d = 0; d < donorCount; d += 1) acc[d]! /= count;
      next.set(v, acc);
      changed = true;
    }
    for (const [v, m] of next) mixes[v] = m;
    if (!changed) break;
  }
  let defaulted = 0;
  for (const v of needsInpaint) {
    if (mixes[v] === null) defaulted += 1;
  }
  return defaulted;
}
